import { useEffect, useMemo, useRef, useState } from 'react';
import type { GenerationAsset } from '../state/types';
import {
  buildMentionCandidates,
  computeMentionChipRanges,
  splitTextByMentionRanges,
  type MentionActorSource,
  type MentionCandidate,
} from '../state/mentionUtils';
import { MentionChipHoverPreview, type MentionChipHoverAnchor } from './MentionChipHoverPreview';
import styles from './MentionTokenChipOverlay.module.css';

// actors 省略時の既定値 (useMentionTokenChips.ts と同じ理由: 毎回新しい配列リテラルを
// 渡すと useMemo の依存が変わり続けるため、安定した参照を1つ共有する)。
const EMPTY_ACTORS: MentionActorSource[] = [];
const EMPTY_CANDIDATES: MentionCandidate[] = [];

// ホバーからプレビュー表示までの遅延。文中を素早くマウスが横切るだけでポップアップが
// ちらつくのを防ぐ (MentionTokenChipOverlay 側の editing 中チップも同じ値で揃える)。
const HOVER_SHOW_DELAY_MS = 150;

type HoverPreviewState = {
  anchor: MentionChipHoverAnchor;
  thumbnailUrl: string | null;
  name: string | null;
  kind: 'material' | 'actor';
};

interface MentionTokenInlineTextProps {
  text: string;
  assets: GenerationAsset[];
  actors?: MentionActorSource[];
  /** 記録由来の他シーンの動画フレーム候補 (buildSceneFrameMentionCandidates)。俳優→素材の後ろに続く。 */
  sceneFrameCandidates?: MentionCandidate[];
}

/**
 * 素材/俳優 @メンショントークン (`@A#3f9c`) を含むテキストの**読み取り表示**
 * (編集モード外) 用。編集中の textarea はオーバーレイ装飾方式 (MentionTokenChipOverlay)
 * だが、読み取り表示は通常の DOM なので座標計測は不要で、テキストを分割してトークン
 * 部分を本物のインライン `<span>` チップに置き換えるだけで済む
 * (docs/features/material-at-mention-plan.md「トークンのチップ表示」節)。
 */
export function MentionTokenInlineText({
  text, assets, actors = EMPTY_ACTORS, sceneFrameCandidates = EMPTY_CANDIDATES,
}: MentionTokenInlineTextProps) {
  const candidates = useMemo(
    () => buildMentionCandidates(assets, actors, sceneFrameCandidates),
    [assets, actors, sceneFrameCandidates],
  );
  const ranges = useMemo(() => computeMentionChipRanges(text, candidates), [text, candidates]);
  const segments = useMemo(() => splitTextByMentionRanges(text, ranges), [text, ranges]);

  // ホバー拡大プレビュー (MentionChipHoverPreview)。表示待ちタイマーだけ ref で持ち、
  // アンマウント時に取り残さないようクリーンアップする。
  const [hoverPreview, setHoverPreview] = useState<HoverPreviewState | null>(null);
  const showTimeoutRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (showTimeoutRef.current !== null) window.clearTimeout(showTimeoutRef.current);
  }, []);

  const handleChipMouseEnter = (event: React.MouseEvent<HTMLSpanElement>, candidate: MentionCandidate) => {
    if (showTimeoutRef.current !== null) window.clearTimeout(showTimeoutRef.current);
    const rect = event.currentTarget.getBoundingClientRect();
    const anchor: MentionChipHoverAnchor = { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
    showTimeoutRef.current = window.setTimeout(() => {
      showTimeoutRef.current = null;
      setHoverPreview({
        anchor,
        thumbnailUrl: candidate.target.thumbnailUrl ?? null,
        name: candidate.target.name ?? null,
        kind: candidate.kind === 'actor' ? 'actor' : 'material',
      });
    }, HOVER_SHOW_DELAY_MS);
  };

  const handleChipMouseLeave = () => {
    if (showTimeoutRef.current !== null) {
      window.clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    setHoverPreview(null);
  };

  return (
    <>
      {segments.map((segment, i) => {
        if (segment.type === 'text') {
          // key はインデックスで十分 (segments は text から毎回再構築される純粋な派生値で、
          // 個々の要素が独立して並び替わることはない)。
          return <span key={i}>{segment.value}</span>;
        }
        const { candidate } = segment.range;
        return (
          <span
            key={i}
            className={styles.inlineChip}
            // title (ネイティブ tooltip) は付けない: ホバープレビューが素材名を表示する
            // ので、二重にツールチップが出るのを避けるためこちらへ一本化する。
            onMouseEnter={(e) => handleChipMouseEnter(e, candidate)}
            onMouseLeave={handleChipMouseLeave}
          >
            {candidate.target.thumbnailUrl && (
              <img
                src={candidate.target.thumbnailUrl}
                alt=""
                draggable={false}
                className={`${styles.thumb} ${candidate.kind === 'actor' ? styles.thumbActor : ''}`}
              />
            )}
            <span className={styles.label}>{segment.range.label}</span>
          </span>
        );
      })}
      {hoverPreview && (
        <MentionChipHoverPreview
          anchor={hoverPreview.anchor}
          thumbnailUrl={hoverPreview.thumbnailUrl}
          name={hoverPreview.name}
          kind={hoverPreview.kind}
        />
      )}
    </>
  );
}
